import { type ReactiveController, type ReactiveElement } from "lit";

/**
 * Reactive controller for handling iOS virtual keyboard behavior.
 *
 * iOS Safari doesn't resize the window when the keyboard appears - it uses the Visual Viewport API instead.
 * This causes layout issues with fixed/fullscreen containers when a text input is focused.
 *
 * This controller:
 * - Detects iOS devices
 * - Subscribes to `visualViewport.resize` events
 * - Provides reactive state for `isKeyboardVisible`, `viewportHeight`, and `keyboardHeight`
 * - Helps fix scroll restoration after keyboard dismissal
 *
 * @example
 * ```typescript
 * class MyComponent extends LitElement {
 *   private keyboardController = new IOSKeyboardController(this);
 *
 *   render() {
 *     const containerStyle = IOSKeyboardController.isIOS && this.keyboardController.isKeyboardVisible
 *       ? `height: ${this.keyboardController.viewportHeight}px`
 *       : '';
 *     return html`<div class="container" style=${containerStyle}>...</div>`;
 *   }
 * }
 * ```
 */
export class IOSKeyboardController implements ReactiveController {
  /**
   * Detect iOS devices using platform/userAgent check.
   * Same pattern used in BodyScrollManager.
   */
  static readonly isIOS =
    /iP(ad|hone|od)/.test(navigator.platform) || (navigator.userAgent.includes("Mac") && "ontouchend" in document);

  private _isKeyboardVisible = false;
  private _viewportHeight = 0;
  private _keyboardHeight = 0;
  private initialHeight = 0;

  /**
   * Threshold in pixels for keyboard detection.
   * A viewport height reduction greater than this indicates the keyboard is open.
   * 150px avoids false positives from URL bar changes while catching keyboard.
   */
  private static readonly KEYBOARD_THRESHOLD = 150;

  constructor(private host: ReactiveElement) {
    this.host.addController(this);
  }

  get isKeyboardVisible(): boolean {
    return this._isKeyboardVisible;
  }

  get viewportHeight(): number {
    return this._viewportHeight;
  }

  get keyboardHeight(): number {
    return this._keyboardHeight;
  }

  hostConnected(): void {
    if (!IOSKeyboardController.isIOS || !window.visualViewport) return;

    this.initialHeight = window.visualViewport.height;
    this._viewportHeight = this.initialHeight;

    window.visualViewport.addEventListener("resize", this.handleResize);
  }

  hostDisconnected(): void {
    if (!IOSKeyboardController.isIOS || !window.visualViewport) return;

    window.visualViewport.removeEventListener("resize", this.handleResize);
  }

  private handleResize = (): void => {
    if (!window.visualViewport) return;

    const currentHeight = window.visualViewport.height;
    const heightDiff = this.initialHeight - currentHeight;

    this._viewportHeight = currentHeight;
    this._keyboardHeight = Math.max(0, heightDiff);
    this._isKeyboardVisible = heightDiff > IOSKeyboardController.KEYBOARD_THRESHOLD;

    this.host.requestUpdate();
  };

  /**
   * Force reflow on a scrollable container to fix iOS Safari scroll lock after keyboard dismissal.
   * Call this from an input blur handler.
   *
   * Uses double RAF to ensure layout completes before restoring scroll.
   *
   * @param container - The scrollable container element
   */
  static fixScrollAfterKeyboardDismiss(container: Element | null | undefined): void {
    if (!container || !IOSKeyboardController.isIOS) return;

    const htmlContainer = container as HTMLElement;
    const scrollTop = htmlContainer.scrollTop;

    requestAnimationFrame(() => {
      htmlContainer.style.overflowY = "hidden";
      requestAnimationFrame(() => {
        htmlContainer.style.overflowY = "auto";
        htmlContainer.scrollTop = scrollTop;
      });
    });
  }

  /**
   * Scroll to bottom after a small delay to ensure input stays visible.
   * Call this from an input focus handler.
   *
   * @param container - The scrollable container element
   * @param delay - Delay in ms before scrolling (default 100ms for keyboard animation)
   */
  static scrollToBottomAfterFocus(container: Element | null | undefined, delay = 100): void {
    if (!container) return;

    setTimeout(() => {
      container.scrollTop = container.scrollHeight;
    }, delay);
  }
}
